import React, { useState } from "react";
import Split from "react-split";
import { Application } from "@microsoft/microsoft-graph-types";
import AppTester from "./AppTester"; // The new side-nav version
import DecodedTokenPanel from "./DecodedTokenPanel";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  Box,
  Typography,
  SpeedDial,
  SpeedDialAction,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import Footer from "./Footer";
import { 
  BrowserRouter as Router, 
  Routes, 
  Route, 
  useSearchParams,
  useNavigate as useRouterNavigate,
  useLocation as useRouterLocation,
  BrowserRouter
} from 'react-router-dom';
// Add these imports at the top of your MainLayout.tsx file
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import SecurityIcon from "@mui/icons-material/Security";
import {
  Card,
  CardContent,
  Grid,
  Chip,
  Tooltip,
  IconButton,
} from "@mui/material";
import Sidebar from "./Sidebar";
import ContentArea from "./ContentArea";
import BugReportIcon from "@mui/icons-material/BugReport";


// Example minimal SAML Tester for demonstration
function SamlTester() {
  const [searchParams] = useSearchParams();
  const assertionParam = searchParams.get("assertion");
  const assertionDecoded = assertionParam
    ? decodeURIComponent(assertionParam)
    : "";

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4">SAML Tester</Typography>
      <Box
        sx={{
          mt: 2,
          p: 2,
          backgroundColor: "#f4f4f4",
          whiteSpace: "pre-wrap",
          fontFamily: "monospace",
          overflow: "auto",
          maxHeight: 300,
        }}
      >
        {assertionDecoded
          ? `Decoded SAML Assertion:\n${assertionDecoded}`
          : "No assertion found. Try SP-Initiated flow."}
      </Box>
    </Box>
  );
}

interface MainLayoutProps {
  isMsalInitialized: boolean;
  userDisplayName: string;
  // Add these optional props for compatibility
  navigate?: (path: string) => void;
  location?: {
    pathname: string;
    search: string;
    hash: string;
  };
  routerProvided?: boolean;
}

const MainLayout: React.FC<MainLayoutProps> = ({
  userDisplayName,
  isMsalInitialized,
}) => {
  // State for the currently selected app (for testing)
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);

  // State for decoded tokens
  const [testedDecodedToken, setTestedDecodedToken] = useState<any>(null);

  // State to control visibility of each decoded token
  const [isTestedTokenShown, setIsTestedTokenShown] = useState<boolean>(false);

  // State for managing app switching confirmation dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingApp, setPendingApp] = useState<Application | null>(null);

  // State for Snackbar notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] =
    useState<"success" | "info" | "warning" | "error">("info");

  // State for active section
  const [activeSection, setActiveSection] = useState<'apps' | 'tools' | 'docs' | null>(null);

  // State for disabling panels
  const [disabledPanels, setDisabledPanels] = useState<{
    leftPanel: boolean;
    topRightPanel: boolean;
    bottomRightPanel: boolean;
  }>({
    leftPanel: false,
    topRightPanel: false,
    bottomRightPanel: false,
  });

  // State to control the visibility of the ContentArea
  const [isContentAreaVisible, setIsContentAreaVisible] = useState(true);

  /************************************************************
   *                  LOCAL STORAGE HELPERS
   ************************************************************/
  /**
   * estimateAvailableStorageKb()
   *  - Tries storing dummy data until we hit QuotaExceededError.
   *  - Returns an approximation in KB.
   */
  function estimateAvailableStorageKB(): number {
    const testPrefix = "__storage_test__";
    const testData = "X".repeat(1024); // 1KB
    let storedKB = 0;

    try {
      // Keep storing until QuotaExceededError
      while (true) {
        localStorage.setItem(testPrefix + storedKB, testData);
        storedKB++;
      }
    } catch (e) {
      // Clean up test items
      for (let i = 0; i < storedKB; i++) {
        localStorage.removeItem(testPrefix + i);
      }
    }
    // At this point we know how many 1KB blocks we stored
    return storedKB;
  }

  // BACKUP HELPERS
  // 1) A small helper to store the app’s current config & timestamp
  function doSilentBackup(app: Application) {
    try {
      // Store the app config under `backup-${app.id}`, same as before
      const key = `backup-${app.id}`;

      // We'll embed a "__backupTimestamp" in the saved object so we can
      // sort by it if we purge the oldest.
      const backupObject = {
        __backupTimestamp: Date.now(),
        ...app,
      };

      const jsonData = JSON.stringify(backupObject, null, 2);
      localStorage.setItem(key, jsonData);

      // Optionally store a separate backup timestamp
      localStorage.setItem(
        `backupTimestamp-${app.id}`,
        new Date().toISOString()
      );

      console.log(`Silent backup done for ${app.displayName}, key: ${key}`);
    } catch (error) {
      console.error("Failed to store silent backup:", error);
    }
  }

  /**
   * 2) Called when an app is selected from AppContent for the first time
   *    or if the user picks the same app again.
   *    If it’s a different app, we open a “Are you sure?” dialog.
   */
  const handleSelectApp = (newApp: Application) => {
    if (selectedApp && selectedApp.id !== newApp.id) {
      // Another app is currently being tested => prompt
      doSilentBackup(selectedApp);
      setPendingApp(newApp);
      setConfirmDialogOpen(true);
    } else {
      // same or no app => switch immediately
      setSelectedApp(newApp);
      setTestedDecodedToken(null);
      setIsTestedTokenShown(false);

      // 3) Perform a silent backup of the newly selected app
      doSilentBackup(newApp);
    }
  };

  /**
   * Handles the closing of the confirmation dialog.
   * If confirmed, switches to the new app and resets the tested decoded token.
   */
  const handleDialogClose = (confirmed: boolean) => {
    setConfirmDialogOpen(false);

    if (confirmed && pendingApp) {
      // User confirmed switching apps
      setSelectedApp(pendingApp);
      setTestedDecodedToken(null); // Hide decoded token from the previous app
      setIsTestedTokenShown(false); // Ensure the tested decoded token panel is hidden

      doSilentBackup(pendingApp);

      // Show a snackbar notification
      setSnackbarMessage(
        `Switched to ${pendingApp.displayName}. Decoded token from the previous app has been hidden.`
      );
      setSnackbarSeverity("info");
      setSnackbarOpen(true);
      setPendingApp(null);
    } else {
      // Clear the pending app regardless of confirmation
      setPendingApp(null);
    }
  };

  /**
   * Handles the reception of the tested account's decoded token from AppTester.
   */
  const handleDecodedTokenTested = (decoded: any) => {
    console.log("MainLayout received decoded tested token");
    setTestedDecodedToken(decoded);
  };

  /**
   * Toggles the visibility of the tested account's decoded token panel.
   */
  const toggleTestedTokenVisibility = () => {
    if (!selectedApp) {
      alert("No application is currently being tested.");
      return;
    }

    if (!testedDecodedToken && !isTestedTokenShown) {
      alert(
        "No decoded token available to display  - select Fetch Access Token!"
      );
      return;
    }

    setIsTestedTokenShown((prev) => !prev);
  };

  /**
   * Handles the closing of the Snackbar notification.
   */
  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <>
      <Routes>
        {/* Route 1: The default path => your split layout */}
        <Route
          path="/"
          element={
            <div style={{ height: "calc(100vh - 64px)", width: "100%" }}>
              {/* Add this Box wrapper with proper closing tag */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  height: "100%",
                  width: "100%",
                }}
              >
                {/* Sidebar */}
                <Sidebar
                  activeSection={activeSection}
                  onSectionChange={setActiveSection}
                  appCount={100} // Replace with actual count from your data
                  isContentAreaVisible={isContentAreaVisible}
                  setIsContentAreaVisible={setIsContentAreaVisible}
                  disabledPanels={disabledPanels}
                  setDisabledPanels={setDisabledPanels}
                />

                <Split
                  direction="horizontal"
                  sizes={isContentAreaVisible ? [35, 65] : [0, 100]}
                  minSize={isContentAreaVisible ? [200, 200] : [0, 200]}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    height: "100%",
                    width: "100%",
                  }}
                  gutterSize={6}
                >
                  {/* Content Area (replacing AppContent) */}
                  <Box
                    sx={{
                      overflow: "hidden",
                      backgroundColor: "#f3f3f3",
                      filter: disabledPanels.leftPanel
                        ? "grayscale(1) opacity(0.6)"
                        : "none",
                      pointerEvents: disabledPanels.leftPanel ? "none" : "auto",
                      transition: "filter 0.3s ease",
                      display: isContentAreaVisible ? "block" : "none", // Conditionally hide the ContentArea
                    }}
                  >
                  <ContentArea
                    activeSection={activeSection}
                    isMsalInitialized={isMsalInitialized}
                    userDisplayName={userDisplayName}
                    onAppSelect={handleSelectApp}
                    onSectionChange={setActiveSection} 
                  />
                  </Box>
                  {/* RIGHT PANE -> Split vertically into AppTester and DecodedTokenPanels */}
                  <Split
                    direction="vertical"
                    sizes={[85, 15]} // Changed from [80, 20] to give more space to tokens
                    minSize={[200, 150]} // Increased minimum size for token panel
                    style={{
                      width: "100%",
                      height: "100%",
                      backgroundColor: "lightgrey",
                    }}
                    gutterSize={6}
                  >
                    {/* Your existing AppTester Box stays the same */}
                    <Box
                      sx={{
                        padding: 2,
                        overflow: "auto",
                        backgroundColor: "#f3f3f3",
                        filter: disabledPanels.topRightPanel
                          ? "grayscale(1) opacity(0.6)"
                          : "none",
                        pointerEvents: disabledPanels.topRightPanel
                          ? "none"
                          : "auto",
                        transition: "filter 0.3s ease",
                      }}
                    >
                      {selectedApp ? (
                        <AppTester
                          key={selectedApp.id}
                          app={selectedApp}
                          onBack={() => setSelectedApp(null)}
                          onDecodedTokenTested={(decoded) => {
                            console.log("MainLayout received decoded tested token");
                            setTestedDecodedToken(decoded);
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            // 🔧 ENHANCED: Subtle gradient background
                            background: "linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)",
                            borderRadius: 1,
                            p: 3,
                            // 🔧 ENHANCED: More sophisticated shadow
                            boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)",
                            position: "relative",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              textAlign: "center",
                              maxWidth: 400,
                              // 🔧 ENHANCED: Subtle animation on load
                              animation: "fadeInUp 0.6s ease-out",
                              "@keyframes fadeInUp": {
                                "0%": {
                                  opacity: 0,
                                  transform: "translateY(20px)",
                                },
                                "100%": {
                                  opacity: 1,
                                  transform: "translateY(0)",
                                },
                              },
                            }}
                          >
                            {/* 🔧 ENHANCED: Icon with subtle background */}
                            <Box
                              sx={{
                                width: 72,
                                height: 72,
                                borderRadius: "50%",
                                background: "linear-gradient(135deg, rgba(25, 118, 210, 0.08), rgba(25, 118, 210, 0.12))",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mb: 2.5,
                                position: "relative",
                                "&::after": {
                                  content: '""',
                                  position: "absolute",
                                  width: "100%",
                                  height: "100%",
                                  borderRadius: "50%",
                                  border: "1px solid rgba(25, 118, 210, 0.1)",
                                },
                              }}
                            >
                              <BugReportIcon
                                sx={{
                                  fontSize: "2.2rem",
                                  color: "primary.main",
                                  filter: "drop-shadow(0 2px 4px rgba(25, 118, 210, 0.15))",
                                }}
                              />
                            </Box>

                            {/* 🔧 ENHANCED: Typography with better hierarchy */}
                            <Typography
                              variant="h5"
                              sx={{
                                mb: 0.5,
                                fontWeight: 600,
                                color: "primary.main",
                                letterSpacing: "-0.01em",
                                // 🔧 ENHANCED: Subtle text shadow for depth
                                textShadow: "0 1px 2px rgba(0,0,0,0.02)",
                              }}
                            >
                              App Tester
                            </Typography>
                            
                            <Typography
                              variant="body2"
                              sx={{
                                mb: 3,
                                color: "text.secondary",
                                fontWeight: 400,
                                // 🔧 ENHANCED: Better line height
                                lineHeight: 1.5,
                              }}
                            >
                              Ready when you are
                            </Typography>

                            {/* 🔧 ENHANCED: Getting Started button with better styling */}
                            <Box
                              sx={{
                                cursor: "pointer",
                                p: 2,
                                borderRadius: 2,
                                // 🔧 ENHANCED: Subtle border and background
                                border: "1px solid rgba(25, 118, 210, 0.15)",
                                background: "linear-gradient(135deg, rgba(25, 118, 210, 0.02), rgba(25, 118, 210, 0.04))",
                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                minWidth: 160,
                                "&:hover": {
                                  backgroundColor: "rgba(25, 118, 210, 0.06)",
                                  borderColor: "rgba(25, 118, 210, 0.25)",
                                  transform: "translateY(-1px)",
                                  boxShadow: "0 4px 12px rgba(25, 118, 210, 0.15)",
                                },
                                "&:active": {
                                  transform: "translateY(0)",
                                },
                              }}
                              onClick={() => {
                                const guide = document.getElementById('quick-guide');
                                const arrow = document.getElementById('guide-arrow');
                                const isExpanded = guide?.style.display !== 'none';
                                
                                if (guide && arrow) {
                                  guide.style.display = isExpanded ? 'none' : 'block';
                                  arrow.style.transform = isExpanded ? 'rotate(0deg)' : 'rotate(90deg)';
                                }
                              }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "primary.main",
                                    fontWeight: 500,
                                    mr: 1,
                                    fontSize: "0.9rem",
                                  }}
                                >
                                  Getting Started
                                </Typography>
                                <ArrowRightIcon
                                  sx={{
                                    color: "primary.main",
                                    fontSize: "1.1rem",
                                    transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                  }}
                                  id="guide-arrow"
                                />
                              </Box>
                            </Box>

                            {/* 🔧 ENHANCED: Guide panel with better styling */}
                            <Box
                              id="quick-guide"
                              sx={{
                                display: "none",
                                mt: 3,
                                // 🔧 ENHANCED: Improved background and borders
                                bgcolor: "rgba(25, 118, 210, 0.03)",
                                border: "1px solid rgba(25, 118, 210, 0.08)",
                                borderRadius: 2,
                                p: 3,
                                width: "100%",
                                maxWidth: 380,
                                // 🔧 ENHANCED: Smoother animation
                                animation: "slideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                                "@keyframes slideDown": {
                                  "0%": {
                                    opacity: 0,
                                    transform: "translateY(-10px)",
                                  },
                                  "100%": {
                                    opacity: 1,
                                    transform: "translateY(0)",
                                  },
                                },
                              }}
                            >
                              {[
                                "Navigate to Applications in the sidebar",
                                "Select an app to configure",
                                "Test authentication flows",
                                "Restore previous app state if needed"
                              ].map((step, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    mb: index < 3 ? 2.5 : 0,
                                    // 🔧 ENHANCED: Subtle animation delay
                                    animation: `fadeInLeft 0.5s ease-out ${index * 0.1}s both`,
                                    "@keyframes fadeInLeft": {
                                      "0%": {
                                        opacity: 0,
                                        transform: "translateX(-10px)",
                                      },
                                      "100%": {
                                        opacity: 1,
                                        transform: "translateX(0)",
                                      },
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      borderRadius: "50%",
                                      // 🔧 ENHANCED: Gradient background for numbers
                                      background: "linear-gradient(135deg, #1976d2, #42a5f5)",
                                      color: "white",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: "0.8rem",
                                      fontWeight: "bold",
                                      mr: 2,
                                      flexShrink: 0,
                                      // 🔧 ENHANCED: Subtle shadow
                                      boxShadow: "0 2px 6px rgba(25, 118, 210, 0.25)",
                                    }}
                                  >
                                    {index + 1}
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "text.secondary",
                                      lineHeight: 1.5,
                                      fontSize: "0.9rem",
                                    }}
                                  >
                                    {step}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </Box>
                      )}
                      </Box>
                    {/* REDESIGNED BOTTOM PANEL */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        backgroundColor: "#f4f8fb",
                        borderTop: "1px solid rgba(0,0,0,0.09)",
                        filter: disabledPanels.bottomRightPanel
                          ? "grayscale(1) opacity(0.6)"
                          : "none",
                        pointerEvents: disabledPanels.bottomRightPanel
                          ? "none"
                          : "auto",
                        transition: "filter 0.3s ease",
                        overflow: "hidden", // Prevent outer overflow
                      }}
                    >
                      {/* Header */}
                      <Box 
                        sx={{ 
                          display: "flex", 
                          alignItems: "center", 
                          p: 2, 
                          pb: 1,
                          borderBottom: "1px solid rgba(0,0,0,0.05)",
                          flexShrink: 0
                        }}
                      >
                        <VpnKeyIcon
                          sx={{
                            mr: 1,
                            color: "primary.main",
                            fontSize: "1.2rem",
                          }}
                        />
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                          }}
                        >
                          Active Tokens
                        </Typography>
                        <Box sx={{ flexGrow: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          {testedDecodedToken ? 1 : 0} active
                        </Typography>
                      </Box>

                      {/* Scrollable Token Container */}
                      <Box 
                        sx={{ 
                          flexGrow: 1, 
                          overflow: "auto",
                          px: 2, 
                          py: 1,
                        }}
                      >
                        {/* Tested App Token Card */}
                        {selectedApp ? (
                          <Box sx={{ mb: 1 }}>
                            <Card
                              variant="outlined"
                              sx={{
                                borderRadius: 2,
                                height: "100%",
                                borderColor: isTestedTokenShown ? "primary.main" : "divider",
                                transition: "all 0.2s ease-in-out",
                                backgroundColor: isTestedTokenShown ? "rgba(25, 118, 210, 0.04)" : "white",
                                maxWidth: "100%",
                                marginX: "auto",
                              }}
                            >
                              <CardContent sx={{ p: "0 !important" }}>
                                <Box
                                  sx={{
                                    p: 1.5,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    borderBottom: isTestedTokenShown ? "1px solid rgba(25, 118, 210, 0.2)" : "none",
                                  }}
                                  onClick={toggleTestedTokenVisibility}
                                  style={{ cursor: "pointer" }}
                                >
                                  <Box 
                                    sx={{ 
                                      display: "flex", 
                                      alignItems: "center", 
                                      maxWidth: "calc(100% - 40px)",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <VpnKeyIcon
                                      sx={{
                                        mr: 1,
                                        color: isTestedTokenShown ? "primary.main" : "text.secondary",
                                        fontSize: "1rem",
                                        flexShrink: 0,
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 500,
                                        color: isTestedTokenShown ? "primary.main" : "text.secondary",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                    >
                                      {selectedApp.displayName}
                                    </Typography>
                                  </Box>

                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleTestedTokenVisibility();
                                    }}
                                    color={isTestedTokenShown ? "primary" : "default"}
                                    sx={{ flexShrink: 0 }}
                                  >
                                    {isTestedTokenShown ? (
                                      <VisibilityOffIcon fontSize="small" />
                                    ) : (
                                      <VisibilityIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Box>

                                {isTestedTokenShown ? (
                                  <Box sx={{ p: 1.5 }}>
                                    <DecodedTokenPanel
                                      decodedToken={testedDecodedToken}
                                      isShown={isTestedTokenShown}
                                      title=""
                                      maxHeight="350px"
                                    />
                                  </Box>
                                ) : (
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <Typography 
                                      variant="caption" 
                                      color="text.secondary"
                                      sx={{
                                        maxWidth: "70%",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                    >
                                      {testedDecodedToken
                                        ? "Click to expand token details"
                                        : "No token available - test authentication"}
                                    </Typography>

                                    {testedDecodedToken && (
                                      <Chip
                                        label={`Scopes: ${testedDecodedToken?.payload?.scp
                                          ? typeof testedDecodedToken.payload.scp === "string"
                                            ? testedDecodedToken.payload.scp.split(" ").length
                                            : "Multiple"
                                          : "None"
                                        }`}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        sx={{ fontSize: "0.7rem", ml: 1, flexShrink: 0 }}
                                      />
                                    )}
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          </Box>
                        ) : (
                          // Enhanced "No App Selected" State
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: '100%',
                              p: 2,
                              textAlign: 'center'
                            }}
                          >
                            <VpnKeyIcon 
                              sx={{ 
                                fontSize: '2rem', 
                                color: 'text.secondary', 
                                mb: 1,
                                opacity: 0.5 
                              }} 
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              Select an app to view tokens
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              Use GraphExplorer for main account token
                            </Typography>
                          </Box>
                        )}            
                      </Box>
                    </Box>
                  </Split>
                </Split>
              </Box>

              {/* Footer */}
              <Footer />

              {/* Confirmation Dialog for App Switching */}
              <Dialog
                open={confirmDialogOpen}
                onClose={() => handleDialogClose(false)}
              >
                <DialogTitle>Switch Application?</DialogTitle>
                <DialogContent>
                  {selectedApp && pendingApp ? (
                    <Typography>
                      You are currently testing{" "}
                      <strong>{selectedApp.displayName}</strong>. <br />
                      Are you sure you want to close it and open{" "}
                      <strong>{pendingApp.displayName}</strong> instead?
                    </Typography>
                  ) : (
                    <Typography>
                      Are you sure you want to switch applications?
                    </Typography>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => {
                      handleDialogClose(false);
                      setPendingApp(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      handleDialogClose(true);
                      if (pendingApp) {
                        setSelectedApp(pendingApp);
                        setTestedDecodedToken(null);
                        setIsTestedTokenShown(false);

                        setSnackbarMessage(
                          `Switched to ${pendingApp.displayName}.`
                        );
                        setSnackbarSeverity("info");
                        setSnackbarOpen(true);
                      }
                      setPendingApp(null);
                    }}
                    variant="contained"
                    color="primary"
                  >
                    Confirm
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Snackbar Notification */}
              <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={(_, reason) => {
                  if (reason !== "clickaway") {
                    setSnackbarOpen(false);
                  }
                }}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              >
                <Alert
                  severity={snackbarSeverity}
                  sx={{ width: "100%" }}
                  onClose={() => setSnackbarOpen(false)}
                >
                  {snackbarMessage}
                </Alert>
              </Snackbar>
            </div>
          }
        />

        {/* Route 2: SAML Tester => used after ACS redirection e.g. /samlTester?assertion=... */}
        <Route path="/samlTester" element={<SamlTester />} />
      </Routes>
    </>
  );
};

export default MainLayout;